// src/contexts/AuthContext.tsx
import { createContext, useContext, useState, ReactNode, useEffect } from "react";

type UserRole = "Faculty" | "Student" | null;

interface AuthContextType {
  role: UserRole;
  initializing: boolean;
  login: (role: UserRole) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [role, setRole] = useState<UserRole>(null);
  const [initializing, setInitializing] = useState(true);

  // Load role from localStorage on first render
  useEffect(() => {
    const storedRole = localStorage.getItem("role") as UserRole;
    if (storedRole) setRole(storedRole);
    setInitializing(false);
  }, []);

  const login = (userRole: UserRole) => {
    setRole(userRole);
    if (userRole) localStorage.setItem("role", userRole);
  };

  const logout = () => {
    setRole(null);
    localStorage.removeItem("role");
    localStorage.removeItem("contactId");
    localStorage.removeItem("usn_number");
    localStorage.removeItem("contact_name");
    // Hard redirect to login to ensure all state is cleared
    window.location.replace("/login");
  };

  return (
    <AuthContext.Provider value={{ role, initializing, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
